import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import {
  Users, Presentation, Home, Boxes, TrendingUp, Wallet, AlertCircle,
  GraduationCap, CalendarCheck, BookOpen, Target, CalendarClock, Bell,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { motion } from "framer-motion";

const Kpi = ({ icon: I, label, value, tone = "primary", i, to }) => {
  const navigate = useNavigate();
  const tones = {
    primary: "bg-sky-50 text-primary", success: "bg-emerald-50 text-emerald-600",
    warning: "bg-amber-50 text-amber-600", violet: "bg-violet-50 text-violet-600",
  };
  const clickable = !!to;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
      <Card
        data-testid={to ? `kpi-card-${to.slice(1).replace(/\//g, "-")}` : undefined}
        onClick={clickable ? () => navigate(to) : undefined}
        role={clickable ? "button" : undefined}
        className={`card-float p-5 border-slate-200 transition-transform duration-200 ${clickable ? "cursor-pointer hover:-translate-y-0.5 hover:border-primary/40" : "hover:-translate-y-0.5"}`}>
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <I className="h-5 w-5" strokeWidth={1.7} />
        </div>
        <div className="mt-4 text-3xl font-display font-bold text-slate-900 tabular-nums">{value}</div>
        <div className="text-sm text-slate-500">{label}</div>
      </Card>
    </motion.div>
  );
};

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="text-slate-400">{t("loading")}</div>;

  const attData = [
    { name: t("present"), value: stats.attendance?.present || 0, c: "#10B981" },
    { name: t("absent"), value: stats.attendance?.absent || 0, c: "#EF4444" },
    { name: t("late"), value: stats.attendance?.late || 0, c: "#F59E0B" },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("dashboard")}</h1>
        <p className="text-sm text-slate-500">{t("welcome")}, {user?.name} — {(user?.roles || []).map((r) => t(`role_${r}`)).join(", ")}</p>
      </div>

      {stats.is_staff ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi i={0} icon={Users} label={t("kpiStudents")} value={stats.students} to="/people/students" />
            <Kpi i={1} icon={Presentation} label={t("kpiTeachers")} value={stats.teachers} tone="violet" to="/people/teachers" />
            <Kpi i={2} icon={Home} label={t("kpiFamilies")} value={stats.families} tone="success" to="/people/families" />
            <Kpi i={3} icon={Boxes} label={t("kpiGroups")} value={stats.groups} tone="warning" to="/school/groups" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <Card className="card-float p-5 border-slate-200 lg:col-span-2">
              <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-4 w-4 text-primary" /><h3 className="font-display font-semibold text-slate-800">{t("revenue")}</h3></div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div><div className="text-xs text-slate-500">{t("billed")}</div><div className="text-xl font-bold tabular-nums text-slate-900">{stats.revenue?.billed?.toLocaleString()}</div></div>
                <div><div className="text-xs text-slate-500">{t("collected")}</div><div className="text-xl font-bold tabular-nums text-emerald-600">{stats.revenue?.collected?.toLocaleString()}</div></div>
                <div><div className="text-xs text-slate-500">{t("outstanding")}</div><div className="text-xl font-bold tabular-nums text-amber-600">{stats.revenue?.outstanding?.toLocaleString()}</div></div>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={stats.revenue_by_month || []}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <Tooltip />
                  <Bar dataKey="amount" fill="#2081C3" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="card-float p-5 border-slate-200">
              <div className="flex items-center gap-2 mb-4"><CalendarCheck className="h-4 w-4 text-primary" /><h3 className="font-display font-semibold text-slate-800">{t("attendance")}</h3></div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={attData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={3}>
                    {attData.map((e, i) => <Cell key={i} fill={e.c} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 text-xs">
                {attData.map((d) => <span key={d.name} className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: d.c }} />{d.name}</span>)}
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi i={0} icon={BookOpen} label={t("homework")} value={stats.homework} tone="primary" to="/pedagogy/homework" />
            <Kpi i={1} icon={AlertCircle} label={t("unpaid")} value={stats.revenue?.unpaid_invoices} tone="warning" to="/finance/invoices" />
            <Kpi i={2} icon={Target} label={t("actionPlans")} value={stats.open_action_plans} tone="violet" />
            <Kpi i={3} icon={CalendarClock} label={t("appointments")} value={stats.pending_appointments} tone="success" />
          </div>
        </>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi i={0} icon={Users} label={t("children")} value={stats.children} />
          <Kpi i={1} icon={GraduationCap} label={t("average")} value={stats.average != null ? `${stats.average}/20` : "—"} tone="violet" />
          <Kpi i={2} icon={BookOpen} label={t("homework")} value={stats.homework} tone="success" />
          <Kpi i={3} icon={Wallet} label={t("outstanding")} value={stats.outstanding?.toLocaleString()} tone="warning" />
          <div className="col-span-2 lg:col-span-4">
            <Card className="card-float p-5 border-slate-200 mt-2">
              <h3 className="font-display font-semibold text-slate-800 mb-4">{t("attendance")}</h3>
              <div className="grid grid-cols-3 gap-4">
                {attData.map((d) => (
                  <div key={d.name} className="text-center p-3 rounded-xl bg-slate-50">
                    <div className="text-2xl font-bold tabular-nums" style={{ color: d.c }}>{d.value}</div>
                    <div className="text-xs text-slate-500">{d.name}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
